import React from 'react';
import LineItemsList, { LineColumn } from '../ui/LineItemsList';
import { formatDateTime } from '../../utils/formatDate';
import { fmtQuantity, wouldGoShort } from '../../utils/asoebiAssignments';
import type { ChecklistLine } from '../../types/ui/AsoebiAssignment';

interface PackingChecklistProps {
  lines: readonly ChecklistLine[];
  /** A viewer: the boxes are drawn but cannot be changed. */
  readOnly: boolean;
  /** The product whose pack/unpack request is in flight. */
  busyProductId: string | null;
  /** One plain sentence per product, shown inside its line. */
  errors: Readonly<Record<string, string>>;
  onToggle: (line: ChecklistLine, packed: boolean) => void;
}

/**
 * What to put in the box for one assignment: a line per item with what is
 * needed, what is in stock and a Packed box. Packing a line takes exactly the
 * needed quantity out of stock, all at once, so a line can never be packed
 * beyond what was ordered; ticking it again does nothing new.
 */
const PackingChecklist: React.FC<PackingChecklistProps> = ({ lines, readOnly, busyProductId, errors, onToggle }) => {
  const columns: LineColumn<ChecklistLine>[] = [
    { key: 'item', header: 'Item', render: line => <span className="aa-line-name">{line.product_name}</span> },
    { key: 'needed', header: 'Needed', render: line => <span>{fmtQuantity(line.quantity)} {line.unit_label}</span> },
    {
      key: 'stock',
      header: 'In stock',
      render: line => (
        <span>
          {line.in_stock === null ? '—' : fmtQuantity(line.in_stock)}
          {wouldGoShort(line) && (
            <span className="aa-short" role="status"> Not enough in stock: packing takes it below zero.</span>
          )}
        </span>
      ),
    },
    {
      key: 'packed',
      header: 'Packed',
      render: (line, ctx) => (
        <input
          type="checkbox"
          className="aa-pack-box"
          checked={line.packed}
          disabled={ctx.disabled}
          aria-label={ctx.ariaLabel}
          aria-describedby={ctx.errorId}
          onChange={e => onToggle(line, e.target.checked)}
        />
      ),
    },
    {
      key: 'by',
      header: 'Packed by',
      render: line => (line.packed
        ? <span>{[line.packed_by, line.packed_at ? formatDateTime(line.packed_at) : null].filter(Boolean).join(', ') || 'Packed'}</span>
        : <span className="aa-muted">—</span>),
    },
  ];

  return (
    <LineItemsList<ChecklistLine>
      label="Packing checklist"
      lines={lines}
      getKey={line => line.product_id}
      lineName={line => line.product_name}
      columns={columns}
      errors={errors}
      disabled={readOnly}
      isLineLocked={line => busyProductId === line.product_id}
      emptyTitle="Nothing to pack"
      emptyHint="This bundle has no items."
    />
  );
};

export default PackingChecklist;
